import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import { styled, useWidget } from '@guest-widgets/core';
import { classes } from '@guest-widgets/shared/src/classes';
import { ProductLink } from '@guest-widgets/shared/src/components/Router/ProductLink';
import { ProductListItem } from '@guest-widgets/shared/src/components/ProductListItem/ProductListItem';
import { ErrorViews, ErrorState } from '@guest-widgets/shared/src/components/ErrorViews';
import { ProductListItemSkeleton } from '@guest-widgets/shared/src/components/ProductListItem/ProductListItemSkeleton';
import { useFeature } from '@guest-widgets/shared/src/contexts/featureContext/featureContext';
import { handleTrackingEvent } from '@guest-widgets/shared/src/utils/customerTracking/utils/trackingEventsHandler';
import { TrackingEventType } from '@guest-widgets/shared/src/utils/customerTracking/types';

import { useProducts } from '../productsContext';
import { useFilters } from '../filtersContext/filtersContext';
import { useFilterAttributes } from '../filterAttributesContext';
import { useContentDescription } from '../useContentDescription';
import { useTrackingProductList } from '../hooks/useTrackingProductList';

export const Products = () => {
  const { widgetArea } = useWidget();
  const {
    products,
    loadProducts,
    companyInformation,
    productsPerPage,
    isLoading: isProductsLoading,
    isError: isProductsError,
  } = useProducts();
  const { numberOfFiltersApplied, filters, resetFilters } = useFilters();
  const { isError: attributeError, loadAttributes } = useFilterAttributes();
  const contentDescription = useContentDescription();
  const { isEnabled } = useFeature();
  const defaultProductSkeletons = widgetArea.isLarge ? 3 : 2;

  useTrackingProductList(productsPerPage ?? [], contentDescription);
  useEffect(() => {
    if (contentDescription) {
      handleTrackingEvent(TrackingEventType.SEARCH_PRODUCTS, contentDescription);
    }
  }, [contentDescription]);

  if (!products) {
    // in case when we have not started loading the products because of missing tags/category attributes
    if (attributeError && filters.categories?.length)
      return (
        <ErrorViews
          type={ErrorState.UnableToLoad}
          onClick={() => {
            loadProducts();
            loadAttributes();
          }}
        />
      );
    return null;
  }

  if (isProductsLoading && (products || []).length === 0) {
    return (
      <ProductItemContainer>
        {Array.from({ length: defaultProductSkeletons }).map((value, index) => (
          <ItemWrapper key={index}>
            <ProductListItemSkeleton />
          </ItemWrapper>
        ))}
      </ProductItemContainer>
    );
  }

  if (isProductsError) {
    return (
      <ErrorViews
        type={ErrorState.UnableToLoad}
        onClick={() => {
          loadProducts();
          loadAttributes();
        }}
      />
    );
  }

  if (products.length === 0) {
    return (
      <ErrorViews
        numberOfFiltersApplied={numberOfFiltersApplied}
        onClick={resetFilters}
        type={ErrorState.Empty}
      />
    );
  }

  const onProductLinkClick = (id: string) => {
    const filtredProducts = (products ?? []).filter((p) => p.id == id);
    if (Array.isArray(filtredProducts) && filtredProducts.length) {
      handleTrackingEvent(TrackingEventType.SELECT_PRODUCT, { products: filtredProducts });
    }
  };

  return (
    <Items className={classes.catalog.list} display="flex" flexWrap="wrap">
      {products
        // Todo: remove this filtering after it is done and deployed on Spine side.
        .filter(({ price }) => price.gross.amount !== null)
        .map(({ id, title, price, images, rating, description, locations, duration, label }) => (
          <ItemWrapper key={id}>
            <ProductLink id={id} onClick={() => onProductLinkClick(id)}>
              <ProductListItem
                title={title}
                rating={rating}
                price={price}
                image={images[0]?.thumbnailUrl}
                description={description}
                location={locations[0]?.city}
                duration={duration}
                label={label}
                isEnabled={isEnabled}
                language={companyInformation?.locale?.language}
              />
            </ProductLink>
          </ItemWrapper>
        ))}
    </Items>
  );
};

const GUEST_WIDGET_THUMBNAIL_WIDTH = 400;

const Items = styled(Box)(({ theme: { spacing }, widgetArea }) => ({
  margin: spacing(widgetArea.basedOnSize(-3, -4, -5)),
}));

const ItemWrapper = styled('div')(({ theme: { spacing }, widgetArea }) => {
  const imageWidth = GUEST_WIDGET_THUMBNAIL_WIDTH;
  const largeScreenSpacing = 5;
  return {
    padding: spacing(widgetArea.basedOnSize(3, 4, largeScreenSpacing)),
    width: widgetArea.basedOnSize('100%', '50%', '33.3%'),
    maxWidth: imageWidth, // prevents the images from stretching
  };
});

const ProductItemContainer = styled('div')(({ theme }) => ({
  margin: theme.spacing(2, -5, 0),
  display: 'flex',
  flexWrap: 'wrap',
}));
